import React, { useState, useEffect, useRef, useCallback } from 'react';
import { COLORS } from '../../utils/constants';
import { tradingAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { Activity, TrendingUp, TrendingDown } from 'lucide-react';

const MarketTrades = ({ symbol }) => {
  const { t } = useLanguage();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(true);
  const tradesRef = useRef(null);
  const isMounted = useRef(true);

  const loadTrades = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      const response = await tradingAPI.getMarketTrades(symbol, 25);
      if (isMounted.current && response.data?.trades) {
        setTrades(response.data.trades);
        setIsLive(true);
      }
    } catch (error) {
      console.error('Failed to load market trades:', error);
      setIsLive(false);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [symbol]);

  useEffect(() => {
    isMounted.current = true;
    loadTrades();
    
    // Update every 2 seconds for realistic feel
    const interval = setInterval(loadTrades, 2000);
    
    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [loadTrades]);

  const formatPrice = (price) => {
    if (price >= 1000) return price.toFixed(2);
    if (price >= 1) return price.toFixed(4);
    return price.toFixed(6);
  };

  const formatQuantity = (qty) => {
    if (qty >= 1000) return qty.toFixed(2);
    if (qty >= 1) return qty.toFixed(4);
    return qty.toFixed(6);
  };

  const formatTime = (timeStr) => {
    const date = new Date(timeStr);
    return date.toLocaleTimeString('ru-RU', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const baseAsset = symbol.replace('USDT', '');

  // Calculate buy/sell counts for summary
  const buyCount = trades.filter(t => t.side === 'buy').length;
  const sellCount = trades.filter(t => t.side === 'sell').length;
  const buyVolume = trades.filter(t => t.side === 'buy').reduce((sum, t) => sum + (t.value || 0), 0);
  const sellVolume = trades.filter(t => t.side === 'sell').reduce((sum, t) => sum + (t.value || 0), 0);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%',
        color: COLORS.textSecondary,
        gap: '8px',
      }}>
        <Activity size={14} className="animate-pulse" />
        Загрузка...
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: COLORS.card, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '6px 10px',
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
      }}>
        <Activity size={12} color={COLORS.primary} />
        <span style={{ color: COLORS.text, fontWeight: '700', fontSize: '11px' }}>
          {t('recentTrades')}
        </span>
        <div style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <span style={{ 
            fontSize: '9px', 
            color: isLive ? COLORS.success : COLORS.textSecondary,
            fontWeight: '500',
          }}>
            LIVE
          </span>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: isLive ? COLORS.success : COLORS.textSecondary,
            animation: isLive ? 'pulse 1.5s infinite' : 'none',
            boxShadow: isLive ? '0 0 6px rgba(14, 203, 129, 0.6)' : 'none',
          }} />
        </div>
      </div>

      {/* Volume Summary */}
      <div style={{
        display: 'flex',
        padding: '4px 10px',
        borderBottom: `1px solid ${COLORS.border}`,
        gap: '8px',
        fontSize: '9px',
        backgroundColor: COLORS.background,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingUp size={10} color={COLORS.success} />
          <span style={{ color: COLORS.success, fontWeight: '600' }}>{buyCount}</span>
          <span style={{ color: COLORS.textSecondary }}>|</span>
          <span style={{ color: COLORS.success, fontFamily: 'monospace' }}>${buyVolume.toFixed(0)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TrendingDown size={10} color={COLORS.danger} />
          <span style={{ color: COLORS.danger, fontWeight: '600' }}>{sellCount}</span>
          <span style={{ color: COLORS.textSecondary }}>|</span>
          <span style={{ color: COLORS.danger, fontFamily: 'monospace' }}>${sellVolume.toFixed(0)}</span>
        </div>
      </div>

      {/* Column Headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 55px',
        padding: '4px 10px',
        borderBottom: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.background,
        flexShrink: 0,
      }}>
        <span style={{ color: COLORS.textSecondary, fontSize: '9px', fontWeight: '600', textTransform: 'uppercase' }}>Цена</span>
        <span style={{ color: COLORS.textSecondary, fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', textAlign: 'right' }}>Кол-во</span>
        <span style={{ color: COLORS.textSecondary, fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', textAlign: 'right' }}>Время</span>
      </div>

      {/* Trades List */}
      <div 
        ref={tradesRef}
        style={{ 
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {trades.map((trade, index) => (
          <div
            key={trade.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 55px',
              padding: '3px 10px',
              borderBottom: `1px solid ${COLORS.border}10`,
              backgroundColor: index === 0 
                ? `${trade.side === 'buy' ? COLORS.success : COLORS.danger}15` 
                : 'transparent',
              transition: 'background-color 0.3s ease',
              animation: index === 0 ? 'fadeIn 0.3s ease' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {trade.side === 'buy' ? (
                <TrendingUp size={8} color={COLORS.success} />
              ) : (
                <TrendingDown size={8} color={COLORS.danger} />
              )}
              <span style={{ 
                color: trade.side === 'buy' ? COLORS.success : COLORS.danger,
                fontSize: '10px',
                fontFamily: 'monospace',
                fontWeight: '600',
              }}>
                {formatPrice(trade.price)}
              </span>
            </div>
            <span style={{ 
              color: COLORS.text, 
              fontSize: '10px', 
              textAlign: 'right',
              fontFamily: 'monospace',
            }}>
              {formatQuantity(trade.quantity)}
            </span>
            <span style={{ 
              color: COLORS.textSecondary, 
              fontSize: '9px', 
              textAlign: 'right',
              fontFamily: 'monospace',
            }}>
              {formatTime(trade.time)}
            </span>
          </div>
        ))}
      </div>

      {/* Pulse animation style */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.9); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MarketTrades;
